import { resolveInternalFlag } from './internalTraffic';

export type BeaconQueueCapture = {
  kind: 'capture';
  event: string;
  properties?: Record<string, unknown>;
  ts: number;
};

export type BeaconQueueIdentify = {
  kind: 'identify';
  distinctId: string;
  properties?: Record<string, unknown>;
  ts: number;
};

export type BeaconQueueReset = { kind: 'reset'; ts: number };

export type BeaconQueueRegister = {
  kind: 'register';
  properties: Record<string, unknown>;
  ts: number;
};

export type BeaconQueueItem =
  | BeaconQueueCapture
  | BeaconQueueIdentify
  | BeaconQueueReset
  | BeaconQueueRegister;

export function posthogPersistenceStorageKey(projectKey: string): string {
  return `ph_${projectKey}_posthog`;
}

/** Reads or seeds the anonymous id PostHog persistence uses so a later SDK init can adopt it. */
export function getOrCreatePosthogDistinctId(projectKey: string): string {
  const storageKey = posthogPersistenceStorageKey(projectKey);
  try {
    const raw = localStorage.getItem(storageKey);
    if (raw) {
      const parsed = JSON.parse(raw) as { distinct_id?: string };
      if (typeof parsed.distinct_id === 'string' && parsed.distinct_id.length > 0) {
        return parsed.distinct_id;
      }
    }
  } catch {
    // ignore
  }

  const distinctId =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `qi_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;

  try {
    localStorage.setItem(
      storageKey,
      JSON.stringify({ distinct_id: distinctId, $device_id: distinctId }),
    );
  } catch {
    // storage blocked — still return an id for this flush
  }
  return distinctId;
}

export function persistPosthogDistinctId(
  projectKey: string,
  distinctId: string,
  options?: { preserveDeviceId?: boolean },
): void {
  const storageKey = posthogPersistenceStorageKey(projectKey);
  try {
    let deviceId = distinctId;
    if (options?.preserveDeviceId) {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw) as { $device_id?: string };
        if (typeof parsed.$device_id === 'string' && parsed.$device_id.length > 0) {
          deviceId = parsed.$device_id;
        }
      }
    }
    localStorage.setItem(
      storageKey,
      JSON.stringify({ distinct_id: distinctId, $device_id: deviceId }),
    );
  } catch {
    // ignore
  }
}

export function isPosthogCapturingOptedOut(projectKey: string): boolean {
  try {
    const raw = localStorage.getItem(posthogPersistenceStorageKey(projectKey));
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { $opt_out?: boolean };
    return parsed.$opt_out === true;
  } catch {
    return false;
  }
}

export type PosthogBatchPayload = {
  api_key: string;
  batch: Array<{
    event: string;
    properties: Record<string, unknown>;
    timestamp: string;
  }>;
};

export function buildPosthogBatchPayload(
  projectKey: string,
  items: readonly BeaconQueueItem[],
): PosthogBatchPayload | null {
  let distinctId = getOrCreatePosthogDistinctId(projectKey);
  const superProps: Record<string, unknown> = {};
  const batch: PosthogBatchPayload['batch'] = [];

  for (const item of items) {
    switch (item.kind) {
      case 'register':
        Object.assign(superProps, item.properties);
        break;
      case 'identify':
        distinctId = item.distinctId;
        persistPosthogDistinctId(projectKey, distinctId, { preserveDeviceId: true });
        break;
      case 'reset': {
        try {
          localStorage.removeItem(posthogPersistenceStorageKey(projectKey));
        } catch {
          // ignore
        }
        distinctId =
          typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : `qi_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
        persistPosthogDistinctId(projectKey, distinctId);
        break;
      }
      case 'capture':
        batch.push({
          event: item.event,
          properties: {
            ...superProps,
            ...(item.properties ?? {}),
            distinct_id: distinctId,
          },
          timestamp: new Date(item.ts).toISOString(),
        });
        break;
      default:
        break;
    }
  }

  if (resolveInternalFlag() && superProps.internal !== true) {
    for (const row of batch) {
      row.properties.internal = true;
    }
  }

  if (batch.length === 0) return null;

  return { api_key: projectKey, batch };
}

export type BeaconTransportResult = {
  sent: boolean;
  via: 'sendBeacon' | 'fetch' | 'none';
  url: string;
  body: string;
};

/** Prefix of queue items that contributed to a beacon batch (includes register/identify/reset before last capture). */
export function queueItemsConsumedByBatchPayload(
  pending: readonly BeaconQueueItem[],
  payload: PosthogBatchPayload,
): BeaconQueueItem[] {
  const consumed: BeaconQueueItem[] = [];
  let capturesInBatch = 0;
  for (const item of pending) {
    consumed.push(item);
    if (item.kind === 'capture') {
      capturesInBatch += 1;
      if (capturesInBatch >= payload.batch.length) {
        break;
      }
    }
  }
  return consumed;
}

export function sendPosthogBatch(
  apiHost: string,
  payload: PosthogBatchPayload,
  transport?: {
    sendBeacon?: (url: string, data: Blob) => boolean;
    fetch?: typeof fetch;
  },
): BeaconTransportResult {
  const host = apiHost.replace(/\/$/, '');
  const url = `${host}/batch/`;
  const body = JSON.stringify(payload);
  const blob = new Blob([body], { type: 'text/plain;charset=UTF-8' });

  try {
    if (transport?.sendBeacon) {
      const ok = transport.sendBeacon(url, blob);
      if (ok) {
        return { sent: true, via: 'sendBeacon', url, body };
      }
    } else if (
      typeof navigator !== 'undefined' &&
      typeof navigator.sendBeacon === 'function'
    ) {
      const ok = navigator.sendBeacon(url, blob);
      if (ok) {
        return { sent: true, via: 'sendBeacon', url, body };
      }
    }

    const fetchFn =
      transport !== undefined
        ? transport.fetch
        : typeof fetch !== 'undefined'
          ? fetch.bind(globalThis)
          : undefined;
    if (fetchFn) {
      void fetchFn(url, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body,
        keepalive: true,
      });
      return { sent: true, via: 'fetch', url, body };
    }

    return { sent: false, via: 'none', url, body };
  } catch {
    return { sent: false, via: 'none', url, body };
  }
}
